import { useEffect, useState } from "react";

import { createTeleopSession } from "../modules/teleop-session";
import type { TeleopSession } from "../modules/teleop-session";
import type { OperatorLogSink } from "../types/log";
import { createOperatorProtocol, createTeleopCommands } from "../modules/teleop-commands";
import type { OperatorProtocol, TeleopCommands } from "../modules/teleop-commands";
import { attachSession, createRobotState, resolveMountSlot } from "../modules/teleop-state";
import type { RobotState } from "../modules/teleop-state";
import type { UseAppConfigResult } from "./useAppConfig";

export interface UseOperatorSessionOptions {
  log: OperatorLogSink;
  /**
   * Opt into a peerId that survives a full page reload (see `teleop-session`'s
   * `persistPeerId` option) — the server sees a reload as the SAME peer reconnecting
   * rather than a new operator. Default (omitted/false): a fresh peerId every mount,
   * which is what's actually needed when more than one independent session can exist
   * in the SAME tab for the SAME room (e.g. a split-screen view) — there's nothing to
   * collide on, by construction, so no manual coordination between callers is needed.
   * Pass `true` only for a page that owns the single connection in its tab.
   */
  persistPeerId?: boolean;
  receiveVideo?: boolean;
}

export interface UseOperatorSessionResult {
  /** The transport session — reads like a store (`useStore(session, selector)`) plus
   * `start`/`stop`/`reconnect`/`sendCommand`. Stable for the whole mount. */
  session: TeleopSession;
  /** Robot business state (config/telemetry/link-quality) — each slice is itself a
   * zustand store. Stable across reconnects, independent of `session`. */
  robotState: RobotState;
  /** Command bus bound to this session's transport. Stable across reconnects. */
  commands: TeleopCommands;
  /** Operator protocol v1 (PROTOCOL.md): raw input stream on `control`, source select /
   * stop on `chat`. One `sid` per page mount. Stable across reconnects. */
  protocol: OperatorProtocol;
}

/**
 * Owns nothing but ORCHESTRATION: create the session + robot-state once, wire them
 * together once, start/stop the session as `config` becomes ready/unmounts. All the
 * actual state (phase/status/videoStalled/robotConfig/telemetry/linkQuality) and
 * behavior (generation-guarding a restart, deriving videoStalled, merging a fatal
 * signaling loss into `phase`) lives inside `teleop-session`/`teleop-state` themselves
 * — see their own module doc comments.
 */
export function useOperatorSession(
  config: UseAppConfigResult,
  { log, persistPeerId, receiveVideo }: UseOperatorSessionOptions
): UseOperatorSessionResult {
  // One trio per session/page mount, built together since each only makes sense wired
  // to the other two. `useState` (not `useMemo`) because these need an ACTUAL lifetime
  // guarantee — `session` is long-lived and its own start()/stop() get called repeatedly
  // across reconnects/config changes without ever being recreated (see teleop-session's
  // module doc comment), and `useMemo` is documented by React as a cache that may be
  // discarded and recomputed, not a stability guarantee. A later change to `persistPeerId`
  // is deliberately ignored, same as before — see the prop's own doc comment.
  const [{ robotState, session, commands, protocol }] = useState(() => {
    const robotState = createRobotState();
    const session = createTeleopSession({
      resolveMountSlot: resolveMountSlot(robotState),
      persistPeerId,
      receiveVideo,
    });
    const commands = createTeleopCommands({
      send: session.sendCommand,
      getSpeedLimits: robotState.speedLimits.getUserSpeedLimits,
      getMotionProfile: robotState.speedLimits.getMotionProfile,
    });

    const protocol = createOperatorProtocol({
      sendReliable: session.sendCommand,
      sendInput: session.sendInput,
    });

    return { robotState, session, commands, protocol };
  });

  // Wires session → robot state (see attachSession's own doc comment) and session →
  // log sink, both for the whole lifetime of `session`/`robotState` — one effect,
  // since both share that same lifecycle and neither is re-wired per reconnect.
  useEffect(() => {
    const detach = attachSession(robotState, session);
    const detachLogs = [
      session.on("log", ({ text, level }) => log.system(text, { level })),
      session.on("operator_message", ({ text }) => log.outgoing(text)),
      session.on("incoming_message", (message) => log.incoming(message)),
    ];

    return () => {
      detach();
      detachLogs.forEach((off) => off());
    };
  }, [robotState, session, log]);

  useEffect(() => {
    if (config.status !== "ready" || !config.sessionId) return;
    const controller = new AbortController();
    void session.start({ sessionId: config.sessionId, config: { ...config, controlSid: protocol.sid }, signal: controller.signal });

    return () => {
      controller.abort();
      void session.stop();
    };
  }, [config, session, protocol]);

  return { session, robotState, commands, protocol };
}
